// Lists (gap #2, technical design §9 saved views): the office's lists of jobs, invoices, estimates
// and customers, run by the server as each person may see them (views.run). Four starters everyone
// has (open jobs, jobs this week, unpaid invoices, follow-ups), the person's own saved lists and the
// shared ones (views.list). A list's statuses, a search, its sort and columns are changed here
// (views.fields says what may be used) and kept with "Save as a list…" (views.save); one's own, or a
// shared one for those who manage settings, is saved again or deleted (views.delete).

import { useMemo, useState } from 'react';
import { Header, type NavProps } from '../Nav.tsx';
import { openJob } from '../job/job-page.ts';
import { openInvoice } from '../invoice/InvoiceSheet.tsx';
import { todayIn } from '../books/model.ts';
import { Empty, Loading, Panel, useAct, useQuery, useToast } from '../books/ui.tsx';
import { SaveButton, useSaveKey } from '../shared/SaveButton.tsx';
import { scrollFocus } from '../shared/scrollFocus.ts';
import { shownText } from '../shared/pages.ts';
import { client } from '../api.ts';
import {
  addViewPage,
  cellText,
  picked,
  sameSpec,
  searched,
  sortedBy,
  starters,
  toggledColumn,
  viewInput,
  withPicked,
  withSearch,
  type EntityInfo,
  type SavedView,
  type ViewResult,
  type ViewSpec,
} from './lists-model.ts';
import '../books/books.css';
import '../shared/office-lists.css';
import './lists.css';
import '../blueprint/inbox.css';
import { useDesign } from '../blueprint/design.ts';

type Chosen = { kind: 'starter'; key: string } | { kind: 'saved'; id: string };

/** Who may share a list with everyone: whoever holds settings.edit, as views.save asks (the owner and managers). */
const SHARE_PERMS = ['settings.edit'];

export function ListsScreen({ nav }: { nav: NavProps }) {
  const timeZone = nav.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const today = todayIn(timeZone);
  const bp = useDesign() === 'blueprint';
  const [toast, setToast] = useToast();
  const fields = useQuery<{ entities: EntityInfo[] }>('views.fields', {});
  const saved = useQuery<{ views: SavedView[] }>('views.list', {});
  const entities = useMemo(
    () => new Map((fields.data?.entities ?? []).map((e) => [e.entity, e])),
    [fields.data],
  );
  // A starter shows only to those who may list its entity (views.fields leaves the others out).
  const mineStarters = useMemo(
    () => starters(today, timeZone).filter((s) => entities.has(s.spec.entity)),
    [today, timeZone, entities],
  );
  const [chosen, setChosen] = useState<Chosen>({ kind: 'starter', key: 'open-jobs' });
  const savedView = chosen.kind === 'saved' ? saved.data?.views.find((v) => v.id === chosen.id) : undefined;
  const base: ViewSpec | null =
    chosen.kind === 'starter'
      ? (mineStarters.find((s) => s.key === chosen.key)?.spec ?? mineStarters[0]?.spec ?? null)
      : savedView
        ? (savedView.spec as unknown as ViewSpec)
        : null;
  const name =
    chosen.kind === 'starter'
      ? (mineStarters.find((s) => s.key === chosen.key)?.name ?? 'Lists')
      : (savedView?.name ?? 'Lists');
  const mine = (saved.data?.views ?? []).filter((v) => v.mine);
  const shared = (saved.data?.views ?? []).filter((v) => !v.mine);
  const pick = (c: Chosen) => setChosen(c);
  const part = (c: Chosen, label: string, key: string) => {
    const on =
      chosen.kind === c.kind &&
      (c.kind === 'starter'
        ? chosen.kind === 'starter' && chosen.key === c.key
        : chosen.kind === 'saved' && chosen.id === c.id);
    return (
      <button
        key={key}
        className={`btn quiet${on ? ' on' : ''}`}
        aria-current={on ? 'page' : undefined}
        onClick={() => pick(c)}
      >
        {label}
      </button>
    );
  };
  return (
    <div className="app books lists">
      <Header
        nav={nav}
        title={name}
        lede="Open jobs, this week, what’s owed and what to follow up. Change one and save it as your own."
      />
      <div className={`books-body${bp ? ' bp-ib' : ''}`}>
        <nav className="books-parts" aria-label="Lists">
          {mineStarters.map((s) => part({ kind: 'starter', key: s.key }, s.name, s.key))}
          {mine.length > 0 && <span className="parts-head">Your lists</span>}
          {mine.map((v) => part({ kind: 'saved', id: v.id }, v.name, v.id))}
          {shared.length > 0 && <span className="parts-head">Shared</span>}
          {shared.map((v) => part({ kind: 'saved', id: v.id }, v.name, v.id))}
        </nav>
        <main className="books-main">
          <div className="books-page">
            {!fields.data || !base || !entities.get(base.entity) ? (
              <Loading
                error={fields.error ?? saved.error}
                what="the lists"
                retry={() => void fields.reload()}
              />
            ) : (
              <ListPanel
                key={chosen.kind === 'starter' ? chosen.key : chosen.id}
                name={name}
                base={base}
                entity={entities.get(base.entity)!}
                saved={savedView}
                canShare={SHARE_PERMS.some((p) => (nav.permissions ?? []).includes(p))}
                timeZone={timeZone}
                toast={setToast}
                onSaved={async (v) => {
                  await saved.reload();
                  if (v) setChosen({ kind: 'saved', id: v.id });
                  else setChosen({ kind: 'starter', key: 'open-jobs' });
                }}
              />
            )}
          </div>
        </main>
      </div>
      {toast && (
        <div className="toast" role="status">
          {toast}
        </div>
      )}
    </div>
  );
}

function ListPanel({
  name,
  base,
  entity,
  saved,
  canShare,
  timeZone,
  toast,
  onSaved,
}: {
  name: string;
  base: ViewSpec;
  entity: EntityInfo;
  saved: SavedView | undefined;
  canShare: boolean;
  timeZone: string;
  toast: (m: string) => void;
  onSaved: (v: SavedView | null) => Promise<void>;
}) {
  // the list as changed here; it starts from its own spec (a new list remounts this panel)
  const [spec, onChange] = useState<ViewSpec>(base);
  const first = useQuery<ViewResult>('views.run', viewInput(spec));
  // the pages "Load more" read on, under the first (list caps L1); a list changed starts again
  const specKey = JSON.stringify(spec);
  const [later, setLater] = useState<{ key: string; of: ViewResult; result: ViewResult } | null>(null);
  const [moreBusy, setMoreBusy] = useState(false);
  const shownResult =
    first.data && later?.key === specKey && later.of === first.data ? later.result : first.data;
  const result = { ...first, data: shownResult };
  const readMore = () => {
    const had = shownResult;
    const of = first.data;
    if (!had?.truncated || !of || moreBusy) return;
    const at = specKey;
    setMoreBusy(true);
    client
      .call<ViewResult>('views.run', viewInput(spec, had.rows.length))
      .then((page) => setLater({ key: at, of, result: addViewPage(had, page) }))
      .catch(() => toast('Couldn’t read the rest of the list. Try again.'))
      .finally(() => setMoreBusy(false));
  };
  const act = useAct(toast, () => undefined);
  const [saving, setSaving] = useState<null | { name: string; shared: boolean }>(null);
  const [deleting, setDeleting] = useState(false);
  const saveKey = useSaveKey(JSON.stringify([saving, spec]));
  const changed = !sameSpec(spec, base);
  const enums = entity.fields.filter((f) => f.type === 'enum' && f.values?.length);
  const searchable = entity.fields.find((f) => f.field === 'customer' || f.field === 'name');
  const shown = result.data?.columns.map((c) => c.field) ?? spec.columns ?? [];
  const sort = spec.sort?.[0];
  const save = async (asNew: boolean) => {
    const r = await act.run<SavedView>(
      'views.save',
      {
        ...(asNew || !saved ? {} : { id: saved.id }),
        name: asNew ? saving!.name : saved!.name,
        spec,
        shared: asNew ? saving!.shared : saved!.shared,
      },
      asNew ? `Saved “${saving!.name}”. It’s under Your lists.` : `Saved “${saved!.name}”.`,
      { idempotencyKey: saveKey.key },
    );
    if (r) {
      saveKey.renew();
      setSaving(null);
      await onSaved(r);
    }
  };
  return (
    <Panel
      title="The list"
      aside={saved?.shared ? <span className="chip say">Shared with everyone</span> : undefined}
    >
      <div className="list-tools">
        {enums.map((f) => {
          const on = picked(spec, f.field) ?? f.values!;
          return (
            <div key={f.field} className="pills" role="group" aria-label={f.label}>
              <span className="flabel">{f.label}</span>
              {f.values!.map((v) => (
                <button
                  key={v}
                  className="pill"
                  aria-pressed={on.includes(v)}
                  onClick={() =>
                    onChange(
                      withPicked(
                        spec,
                        f.field,
                        on.includes(v) ? on.filter((x) => x !== v) : [...on, v],
                        f.values!,
                      ),
                    )
                  }
                >
                  {cellText(entity.entity, { field: f.field, type: 'enum' }, v, timeZone)}
                </button>
              ))}
            </div>
          );
        })}
        {searchable && (
          <label className="inline-label">
            <span className="flabel">{searchable.label} contains</span>
            <input
              className="field"
              defaultValue={searched(spec, searchable.field)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onChange(withSearch(spec, searchable.field, e.currentTarget.value));
              }}
              onBlur={(e) => onChange(withSearch(spec, searchable.field, e.currentTarget.value))}
            />
          </label>
        )}
        <details className="list-columns">
          <summary>Columns</summary>
          <div className="pills" role="group" aria-label="Columns">
            {entity.fields.map((f) => (
              <button
                key={f.field}
                className="pill"
                aria-pressed={shown.includes(f.field)}
                onClick={() => onChange(toggledColumn(spec, f.field, shown))}
              >
                {f.label}
              </button>
            ))}
          </div>
        </details>
      </div>

      {!result.data ? (
        <Loading
          error={result.error}
          what="the list"
          kind="table"
          cols={entity.fields.filter((f) => (spec.columns ?? []).includes(f.field)).map((f) => f.label)}
          retry={() => void result.reload()}
        />
      ) : result.data.rows.length === 0 ? (
        <Empty>Nothing on this list right now.</Empty>
      ) : (
        <div className="table-wrap" ref={scrollFocus}>
          <table className="grid-table" aria-label={name}>
            <thead>
              <tr>
                {result.data.columns.map((c) => (
                  <th
                    key={c.field}
                    aria-sort={
                      sort?.field === c.field ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined
                    }
                    className={c.type === 'money' ? 'num' : undefined}
                  >
                    <button className="th-sort" onClick={() => onChange(sortedBy(spec, c.field))}>
                      {c.label}
                      {sort?.field === c.field ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {result.data.rows.map((r) => (
                <tr key={String(r['id'])}>
                  {result.data!.columns.map((c) => (
                    <td
                      key={c.field}
                      className={
                        c.type === 'money'
                          ? 'num'
                          : c.type !== 'text' || c.field === 'number' || c.field === 'job'
                            ? 'nowrap'
                            : undefined
                      }
                    >
                      {/* a job's number opens its sheet (openJob), an invoice's its own (openInvoice) */}
                      {entity.entity === 'jobs' && c.field === 'number' ? (
                        <button className="cell-link" onClick={() => openJob(String(r['id']))}>
                          {cellText(entity.entity, c, r[c.field], timeZone)}
                        </button>
                      ) : entity.entity === 'invoices' && c.field === 'number' ? (
                        <button className="cell-link" onClick={() => openInvoice(String(r['id']))}>
                          {cellText(entity.entity, c, r[c.field], timeZone)}
                        </button>
                      ) : (
                        cellText(entity.entity, c, r[c.field], timeZone)
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {result.data?.truncated && (
        <p className="list-more">
          <span className="muted small">
            {shownText(result.data.rows.length, result.data.total ?? result.data.rows.length)}
          </span>
          <button type="button" className="btn soft" disabled={moreBusy} onClick={readMore}>
            Load more
          </button>
        </p>
      )}

      <div className="list-save">
        {saving ? (
          <div className="fgrid inline-form">
            <label className="wide">
              <span className="flabel">Name</span>
              <input
                className="field"
                value={saving.name}
                autoFocus
                onChange={(e) => setSaving({ ...saving, name: e.target.value })}
              />
            </label>
            {canShare && (
              <label className="checkrow">
                <input
                  type="checkbox"
                  checked={saving.shared}
                  onChange={(e) => setSaving({ ...saving, shared: e.target.checked })}
                />{' '}
                Share with everyone
              </label>
            )}
            <div className="row">
              <SaveButton
                busy={act.busy}
                busyLabel="Saving…"
                disabled={!saving.name.trim()}
                onClick={() => void save(true)}
              >
                Save the list
              </SaveButton>
              <button className="btn quiet" onClick={() => setSaving(null)}>
                Cancel
              </button>
            </div>
          </div>
        ) : deleting && saved ? (
          <div className="row">
            <span>
              Delete “{saved.name}”{saved.shared ? ' for everyone' : ''}?
            </span>
            <SaveButton
              className="btn danger"
              busy={act.busy}
              busyLabel="Deleting…"
              onClick={() =>
                void act
                  .run('views.delete', { id: saved.id }, `Deleted “${saved.name}”.`)
                  .then((r) => (r ? onSaved(null) : undefined))
              }
            >
              Delete
            </SaveButton>
            <button className="btn quiet" onClick={() => setDeleting(false)}>
              Keep it
            </button>
          </div>
        ) : (
          <div className="row">
            {saved?.canEdit && changed && (
              <SaveButton busy={act.busy} busyLabel="Saving…" onClick={() => void save(false)}>
                Save changes
              </SaveButton>
            )}
            {(changed || !saved) && (
              <button
                className={`btn ${saved?.canEdit && changed ? 'soft' : 'primary'}`}
                onClick={() => setSaving({ name: saved ? `${saved.name} (copy)` : name, shared: false })}
              >
                Save as a list…
              </button>
            )}
            {changed && (
              <button className="btn quiet" onClick={() => onChange(base)}>
                Undo changes
              </button>
            )}
            {saved?.canEdit && (
              <button className="btn quiet" onClick={() => setDeleting(true)}>
                Delete list
              </button>
            )}
          </div>
        )}
      </div>
    </Panel>
  );
}
